import { Component, NgZone, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx';
import { NavController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { AuthService } from './services/auth.service';
import { ChatService } from './services/chat.service';
import { DataService } from './services/data.service';
import { OneSignalService } from './services/one-signal.service';
import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { FirebaseService } from './services/firebase.service';
import { ModalMakerService } from './services/modal-maker.service';
import { HotSupportChatStatus } from './models/hot-support-chat-status';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  subdomain: string = null;
  refresh = false;

  constructor(
    private router: Router,
    public dataServ: DataService,
    private authServ: AuthService,
    private chatServ: ChatService,
    private zone: NgZone,
    private platform: Platform,
    private screenLock: ScreenOrientation,
    private oneSignalServ: OneSignalService,
    private translateServ: TranslateService,
    private firebaseService: FirebaseService,
    private modalServ: ModalMakerService
  ) {
    // For AppUrl
    App.addListener('appUrlOpen', (data: any) => {
      this.zone.run(() => {
        const route = data.url.split('.app').pop();
        if (route) {
          if (route.includes('?roomno=')) {
            const roomno = route.split('?roomno=')[1];
            localStorage.setItem('roomno', roomno);
          }
          router.navigateByUrl(route);
        }
      });
    });

    this.initializeApp();
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.platform.pause.subscribe(() => {
        this.sendOfflineStatus();
      });
    });
  }

  async ngOnInit() {
    try {
      this.subscribeRouterEvents();

      await this.dataServ.init();

      if (!localStorage.getItem('rData')) {
        this.router.navigate(['/hotels']);
      }

      if (this.platform.is('capacitor')) {
        this.checkForUpdate();
      }

      if (!localStorage.getItem('userLang')) {
        const userLang = navigator.language.slice(0, 2).toLowerCase();
        localStorage.setItem('userLang', userLang);
        this.translateServ.setDefaultLang(userLang);
        this.refresh = true;

        setTimeout(() => {
          this.refresh = false;
        }, 500);
      }

      const urlParams = new URLSearchParams(window.location.search);
      const myParam = urlParams.get('roomno');
      if (myParam) {
        localStorage.setItem('roomno', myParam);
      }

      const URL = window.location.hostname.split('.');
      const SUBDOMAIN = URL[0];

      await this.firebaseService.getFirebaseConfig(environment.hotelId);

      if (SUBDOMAIN !== 'www' && SUBDOMAIN !== '192') {
        // Altınyunus kontrolü
        if (SUBDOMAIN === 'asistan') {
          environment.hotelId = 640;
          localStorage.setItem('hotelId', '640');
          await this.dataServ.getRichData(640);
          this.dataServ.removeDateRange(this.dataServ.baseData);
        }

        // Firebase başlatma
        if (this.firebaseService.FIREBASECONFIG) {
          const firebaseConfig = this.firebaseService.FIREBASECONFIG;
          try {
            const app = initializeApp(firebaseConfig);
            getAnalytics(app); // Analytics kullanımı
          } catch (error) {
            console.error('Firebase initialization error:', error);
          }
        }

        // iOS için Crashlytics kontrolü
        if (this.platform.is('ios')) {
          await FirebaseCrashlytics.isEnabled().then((isEnabled) => {
            if (isEnabled) {
              console.log('Crashlytics is enabled.');
            } else {
              console.log('Crashlytics is not enabled.');
            }
          });
        }
      }

      // Push bildirimleri
      if (this.platform.is('capacitor')) {
        this.oneSignalServ.cordovapush();
      } else {
        const ONESIGNAL = window['OneSignal'] || [];

        ONESIGNAL.push(() => {
          ONESIGNAL.init({
            appId: this.dataServ.rData?.one_signal_app_id,
            autoRegister: false,
            allowLocalhostAsSecureOrigin: true,
            notifyButton: { enable: false },
          });
        });

        ONESIGNAL.push(() => {
          ONESIGNAL.push(['registerForPushNotifications']);
        });

        ONESIGNAL.push(() => {
          ONESIGNAL.on('subscriptionChange', (isSubscribed) => {
            ONESIGNAL.getUserId().then((userId) => {
              localStorage.setItem('oneSignal', userId);
              if (isSubscribed && localStorage.getItem('islogged')) {
                this.oneSignalServ.oneSignalLogin();
              } else {
                this.oneSignalServ.oneSignalLogout();
              }
            });
          });

          ONESIGNAL.on('notificationDisplay', (event) => {
            if (event) {
              this.oneSignalServ.notificationEvent(event.data);
            }
          });
        });
      }

      // Ekran kilidi ayarı
      if (this.platform.is('capacitor')) {
        this.screenLock.lock(this.screenLock.ORIENTATIONS.PORTRAIT);
      }
    } catch (error) {
    }
  }

  sendOfflineStatus() {
    const chatStatus = new HotSupportChatStatus();
    chatStatus.type = 'HotSupport';
    chatStatus.is_online = false;
    this.chatServ.postChatStatus(this.authServ.profile.id, chatStatus);
  }

  compareVersions(version1: string, version2: string): number {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1 = v1Parts[i] || 0;
      const v2 = v2Parts[i] || 0;

      if (v1 > v2) {
        return 1;
      } else if (v1 < v2) {
        return -1;
      }
    }

    return 0;
  }

  async checkForUpdate() {
    const RESP = this.compareVersions(
      this.dataServ.rData?.minimum_app_version,
      environment.version
    );

    if (RESP === 1) {
      this.modalServ.versionControl();
    }
  }

  private subscribeRouterEvents() {
    this.router.events.subscribe((ev) => {
      const url = (ev as any).url;
      if (this.refresh) {
        return;
      }

      if (!this.dataServ.rData && url && !url.includes('/hotels')) {
        return;
      }

      if (
        ev instanceof NavigationStart &&
        url &&
        !url.includes('/welcome') &&
        !url.includes('/download') &&
        !url.match(/([/]+(?=home))\w+([/][0-9])\w+([/][0-9])\w+/g)
      ) {
        const EL = document.getElementById('router-el');
        EL.style.opacity = '0';
        this.dataServ.versionControl();
        this.authServ.control();
      }

      this.authServ.control();

      if (ev instanceof NavigationEnd) {
        const EL = document.getElementById('router-el');
        EL.style.opacity = '1';

        if (this.authServ.isLogged) {
          this.chatServ.getHeaders();
        }
      }
    });
  }
}
